import * as joint from '@joint/core';
import { FlowPreviewOptions } from './types';
import { applyGraphBackCompatAndCleanup } from './graph-compat';
import type { ChartResizeDependencies } from '../ai-charts-resize';

/** Fits every node and link into the paper, centred both ways, without blowing small graphs up past 2x. */
export const PREVIEW_FIT_OPTIONS = { verticalAlign: 'middle', horizontalAlign: 'middle', maxScale: 2 } as const;

export interface PreviewSize {
    width: number;
    height: number;
}

export interface PreviewPadding {
    top: number;
    right: number;
    bottom: number;
    left: number;
}

/**
 * The padding that keeps a drawing clear of a box over the container's top-left corner: either
 * beside the box or below it, whichever lets the content scale larger. With no box, or one outside
 * the container, it is the plain margin all round. The margin is also the gap kept from the box.
 *
 * @param container The container's size.
 * @param content The drawing's size at scale 1.
 * @param reserved How far the box reaches into the container from its top-left corner.
 */
export function paddingAround(
    container: PreviewSize,
    content: PreviewSize,
    reserved: PreviewSize | null,
    margin: number,
    maxScale: number = PREVIEW_FIT_OPTIONS.maxScale,
): PreviewPadding {
    const plain = { top: margin, right: margin, bottom: margin, left: margin };
    if (!reserved || reserved.width <= 0 || reserved.height <= 0 || content.width <= 0 || content.height <= 0) {
        return plain;
    }

    const beside = { ...plain, left: Math.max(margin, reserved.width + margin) };
    const below = { ...plain, top: Math.max(margin, reserved.height + margin) };

    const scaleWith = (padding: PreviewPadding) => Math.min(
        maxScale,
        (container.width - padding.left - padding.right) / content.width,
        (container.height - padding.top - padding.bottom) / content.height,
    );

    // Below wins a tie: a wide flow in a tall slot then keeps its full width and only moves down.
    return scaleWith(beside) > scaleWith(below) ? beside : below;
}

/** What Blazor holds on to so it can tear a preview down. */
export interface FlowPreviewHandle {
    dispose(): void;
}

/**
 * The container's usable size, or null while it has none — hidden, detached, or caught mid-reflow
 * during a drag. A zero-size fit would collapse the drawing, so callers keep the last good layout.
 */
export function measurePreviewContainer(container: HTMLElement): PreviewSize | null {
    const width = Math.round(container.clientWidth);
    const height = Math.round(container.clientHeight);
    return width > 0 && height > 0 ? { width, height } : null;
}

/**
 * Observes a container and reports meaningful size changes at most once per frame. Zero-size
 * measurements are ignored. Returns a disposer that also cancels a pending measurement.
 */
export function observePreviewSize(
    container: HTMLElement,
    initialSize: PreviewSize | null,
    onSizeChanged: (size: PreviewSize) => void,
    dependencies?: ChartResizeDependencies | null,
): () => void {
    const environment = dependencies === undefined ? browserDependencies() : dependencies;
    if (!environment) return () => { };

    let currentSize = initialSize;
    let pendingFrame: number | null = null;
    let disposed = false;

    const observer = environment.createObserver(() => {
        if (disposed || pendingFrame !== null) return;

        pendingFrame = environment.requestFrame(() => {
            pendingFrame = null;
            if (disposed) return;

            const nextSize = measurePreviewContainer(container);
            if (!nextSize) return;
            if (currentSize && nextSize.width === currentSize.width && nextSize.height === currentSize.height) return;

            currentSize = nextSize;
            onSizeChanged(nextSize);
        });
    });

    observer.observe(container);

    return () => {
        if (disposed) return;
        disposed = true;
        observer.disconnect();

        if (pendingFrame !== null) {
            environment.cancelFrame(pendingFrame);
            pendingFrame = null;
        }
    };
}

export interface PreviewRect {
    x: number;
    y: number;
    width: number;
    height: number;
}

/** What to do with a drawing that has just been fitted to the whole container. */
export type Placement =
    | { kind: 'stay' }
    | { kind: 'slide'; dx: number; dy: number }
    | { kind: 'shrink' };

/**
 * Moves a drawing as little as it can to clear a box over the container's top-left corner. It stays
 * where it is when it already clears the box. Otherwise it slides right or down — whichever is the
 * shorter move — at its full size, provided it stays wholly inside the container's margin. Failing
 * both, it is shrunk to fit beside or below the box when `canShrink` allows,
 * and otherwise stays put with the box floating over it.
 *
 * @param container The container's size.
 * @param content Where the fitted drawing sits in the container.
 * @param reserved How far the box reaches into the container from its top-left corner.
 */
export function placeClearOf(
    container: PreviewSize,
    content: PreviewRect,
    reserved: PreviewSize,
    canShrink: boolean,
    margin: number,
): Placement {
    const clearX = reserved.width + margin;
    const clearY = reserved.height + margin;

    if (content.x >= clearX || content.y >= clearY) return { kind: 'stay' };

    const dx = clearX - content.x;
    const dy = clearY - content.y;
    const canSlideRight = content.x + content.width + dx <= container.width - margin;
    const canSlideDown = content.y + content.height + dy <= container.height - margin;

    if (canSlideRight && (!canSlideDown || dx <= dy)) return { kind: 'slide', dx, dy: 0 };
    if (canSlideDown) return { kind: 'slide', dx: 0, dy };

    return canShrink ? { kind: 'shrink' } : { kind: 'stay' };
}

/**
 * Calls back at most once per frame whenever an element is resized. Returns a disposer that also
 * cancels a pending call.
 */
function observeChanges(
    target: HTMLElement,
    onChange: () => void,
    dependencies?: ChartResizeDependencies | null,
): () => void {
    const environment = dependencies === undefined ? browserDependencies() : dependencies;
    if (!environment) return () => { };

    let pendingFrame: number | null = null;
    let disposed = false;

    const observer = environment.createObserver(() => {
        if (disposed || pendingFrame !== null) return;

        pendingFrame = environment.requestFrame(() => {
            pendingFrame = null;
            if (!disposed) onChange();
        });
    });

    observer.observe(target);

    return () => {
        if (disposed) return;
        disposed = true;
        observer.disconnect();

        if (pendingFrame !== null) {
            environment.cancelFrame(pendingFrame);
            pendingFrame = null;
        }
    };
}

const activePreviews = new WeakMap<HTMLElement, FlowPreview>();

export class FlowPreview implements FlowPreviewHandle {
    private readonly namespace = joint.shapes;
    private readonly container: HTMLElement;
    private readonly paper: joint.dia.Paper;
    private readonly graph: joint.dia.Graph;
    private readonly stopObserving: () => void;
    private readonly stopObservingAvoided: () => void;
    private readonly avoidElementId: string | null;
    private readonly shrinkToAvoid: boolean;
    private readonly margin: number;
    private disposed = false;

    constructor(options: FlowPreviewOptions, resizeDependencies?: ChartResizeDependencies | null) {
        const graphJson = JSON.parse(options.graphJson);

        this.container = options.container;
        this.avoidElementId = options.avoidElementId || null;
        this.shrinkToAvoid = options.shrinkToAvoid === true;
        this.margin = Math.max(0, options.margin ?? 0);
        this.graph = new joint.dia.Graph({}, { cellNamespace: this.namespace });

        const paperElement = this.container.ownerDocument.createElement('div');
        paperElement.style.position = 'absolute';
        paperElement.style.left = '0';
        paperElement.style.top = '0';
        if (this.container.ownerDocument.defaultView?.getComputedStyle(this.container).position === 'static') {
            this.container.style.position = 'relative';
        }
        this.container.appendChild(paperElement);

        const size = measurePreviewContainer(this.container);
        this.paper = new joint.dia.Paper({
            el: paperElement,
            model: this.graph,
            width: size?.width ?? 0,
            height: size?.height ?? 0,
            interactive: false,
            cellViewNamespace: this.namespace,
        });

        try {
            this.paper.model.fromJSON(graphJson, { cellNamespace: this.namespace });
            // Apply the same backward compatibility and cleanup logic used by FlowEditor
            applyGraphBackCompatAndCleanup(this.graph);
            this.paper.model.fromJSON(this.graph.toJSON(), { cellNamespace: this.namespace });
        } catch (error) {
            this.paper.remove();
            throw error;
        }

        if (size) this.fit();

        this.stopObserving = observePreviewSize(this.container, size, next => this.resize(next), resizeDependencies);

        // The box being avoided can change size on its own — fonts loading, the app's root font
        // size following the window — so it gets its own watch.
        const avoided = this.findAvoided();
        this.stopObservingAvoided = avoided
            ? observeChanges(avoided, () => this.refit(), resizeDependencies)
            : () => { };
    }

    resize(size: PreviewSize): void {
        if (this.disposed) return;
        this.paper.setDimensions(size.width, size.height);
        this.fit();
    }

    dispose(): void {
        if (this.disposed) return;
        this.disposed = true;

        this.stopObserving();
        this.stopObservingAvoided();
        this.paper.remove();

        if (activePreviews.get(this.container) === this) {
            activePreviews.delete(this.container);
        }
    }

    private refit(): void {
        if (this.disposed || !measurePreviewContainer(this.container)) return;
        this.fit();
    }

    private fit(): void {
        // Without a margin this is exactly the fit every preview has always had.
        this.paper.transformToFitContent(this.margin ? { ...PREVIEW_FIT_OPTIONS, padding: this.margin } : { ...PREVIEW_FIT_OPTIONS });

        const reserved = this.reservedCorner();
        const size = reserved ? measurePreviewContainer(this.container) : null;
        if (!reserved || !size) return;

        const placement = placeClearOf(size, this.paper.getContentBBox(), reserved, this.shrinkToAvoid, this.margin);

        if (placement.kind === 'slide') {
            const { tx, ty } = this.paper.translate();
            this.paper.translate(tx + placement.dx, ty + placement.dy);
        } else if (placement.kind === 'shrink') {
            const area = this.paper.getContentArea();
            const padding = paddingAround(size, { width: area.width, height: area.height }, reserved, this.margin);
            this.paper.transformToFitContent({ ...PREVIEW_FIT_OPTIONS, padding });
        }
    }

    private findAvoided(): HTMLElement | null {
        return this.avoidElementId ? this.container.ownerDocument.getElementById(this.avoidElementId) : null;
    }

    /** How far the avoided box reaches into the container from its top-left, or null when it is not on screen. */
    private reservedCorner(): PreviewSize | null {
        const avoided = this.findAvoided();
        if (!avoided) return null;

        const box = avoided.getBoundingClientRect();
        if (box.width <= 0 || box.height <= 0) return null;

        const bounds = this.container.getBoundingClientRect();
        const width = Math.round(box.right - bounds.left);
        const height = Math.round(box.bottom - bounds.top);
        return width > 0 && height > 0 ? { width, height } : null;
    }
}

/** Draws a flow into a container, replacing any preview already there. Always returns a handle. */
export function createFlowPreview(options: FlowPreviewOptions, resizeDependencies?: ChartResizeDependencies | null): FlowPreviewHandle {
    activePreviews.get(options.container)?.dispose();

    try {
        const preview = new FlowPreview(options, resizeDependencies);
        activePreviews.set(options.container, preview);
        return preview;
    } catch (error) {
        console.error('Invalid JSON input', error);
        console.error('Options:', options);
        return { dispose: () => { } };
    }
}

function browserDependencies(): ChartResizeDependencies | null {
    if (typeof ResizeObserver === 'undefined' || typeof window === 'undefined') return null;

    return {
        createObserver: callback => new ResizeObserver(callback),
        requestFrame: callback => window.requestAnimationFrame(callback),
        cancelFrame: handle => window.cancelAnimationFrame(handle),
    };
}
